import { type ComputedRef, type Ref, ref } from 'vue'
import { ChatApiService, type ChatMemberResponse } from '../services/chatApiService'
import { getCurrentMatrixUserId } from '../services/matrixChatClient'
import { useChatStore as useChatStoreCore } from '@/modules/chat-matrix-fast/stores/chatStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import type { IEmployee } from '@/modules/apiStore/types/employees/IEmployee'

interface UseGroupMemberActionsOptions {
  groupId: Ref<number | undefined>
  matrixRoomId?: Ref<string | undefined> | ComputedRef<string | undefined>
  resolvedMatrixRoomId?: Ref<string | undefined> | ComputedRef<string | undefined>
  members: Ref<ChatMemberResponse[]>
  totalMembersCount: Ref<number | null>
  creatorUserId?: Ref<string | null | undefined> | ComputedRef<string | null | undefined>
  loadChatMembers: (reset?: boolean) => Promise<void>
  fetchAllChatMembers: () => Promise<ChatMemberResponse[]>
  onUpdated: () => void
}

function getMemberTrackingId(member: ChatMemberResponse): string {
  return member.user_uuid || String(member.user.id)
}

/** Удаление участников через backend remove-member */
export function useGroupMemberActions(options: UseGroupMemberActionsOptions) {
  const {
    groupId,
    members,
    totalMembersCount,
    creatorUserId,
    loadChatMembers,
    fetchAllChatMembers,
    onUpdated,
  } = options
  const feedbackStore = useFeedbackStore()
  const coreChatStore = useChatStoreCore()
  const removingMemberIds = ref<Set<string>>(new Set())
  const roleChangingIds = ref<Set<string>>(new Set())

  function isRemovingMember(member: ChatMemberResponse) {
    return removingMemberIds.value.has(getMemberTrackingId(member))
  }

  function isCreator(member: ChatMemberResponse) {
    const creatorId = creatorUserId?.value
    if (!creatorId) {
      return false
    }

    return String(member.user.id) === String(creatorId)
  }

  function isRoleChanging(member: ChatMemberResponse) {
    return roleChangingIds.value.has(getMemberTrackingId(member))
  }

  async function setMemberAdmin(member: ChatMemberResponse, isAdmin: boolean) {
    const chatId = groupId.value
    if (!chatId) {
      feedbackStore.showToast({ type: 'error', message: 'Не удалось определить чат', time: 3000 })
      return
    }

    if (isCreator(member) && !isAdmin) {
      feedbackStore.showToast({ type: 'warning', message: 'Нельзя снять права с создателя', time: 3000 })
      return
    }

    const trackingId = getMemberTrackingId(member)
    roleChangingIds.value.add(trackingId)

    try {
      await ChatApiService.setAdmin(chatId, String(member.user.id), isAdmin)
      const target = members.value.find(item => getMemberTrackingId(item) === trackingId)
      if (target) {
        target.is_admin = isAdmin
      }
      feedbackStore.showToast({
        type: 'success',
        message: isAdmin ? 'Участник назначен админом' : 'Права админа сняты',
        time: 3000,
      })
      await loadChatMembers(true)
      onUpdated()
    } catch {
      feedbackStore.showToast({
        type: 'error',
        message: isAdmin ? 'Не удалось назначить админа' : 'Не удалось снять права админа',
        time: 3000,
      })
    } finally {
      roleChangingIds.value.delete(trackingId)
    }
  }

  async function removeMember(member: ChatMemberResponse) {
    const chatId = groupId.value
    if (!chatId) {
      feedbackStore.showToast({ type: 'error', message: 'Не удалось определить чат', time: 3000 })
      return
    }

    const trackingId = getMemberTrackingId(member)
    const memberId = String(member.user.id)
    const myMxid = getCurrentMatrixUserId()
    const memberMatrixId = member.user_uuid || memberId

    if (myMxid && memberMatrixId === myMxid) {
      feedbackStore.showToast({ type: 'warning', message: 'Нельзя удалить себя из группы', time: 3000 })
      return
    }

    removingMemberIds.value.add(trackingId)

    try {
      await coreChatStore.removeMemberFromChat(chatId, memberId)

      members.value = members.value.filter(
        item => getMemberTrackingId(item) !== trackingId,
      )
      if (totalMembersCount.value !== null && totalMembersCount.value > 0) {
        totalMembersCount.value -= 1
      }

      feedbackStore.showToast({ type: 'success', message: 'Участник удален из группы', time: 3000 })
      await loadChatMembers(true)
      onUpdated()
    } catch {
      feedbackStore.showToast({ type: 'error', message: 'Не удалось удалить участника', time: 3000 })
    } finally {
      removingMemberIds.value.delete(trackingId)
    }
  }

  async function removeAll() {
    const chatId = groupId.value
    if (!chatId) {
      feedbackStore.showToast({ type: 'error', message: 'Не удалось определить чат', time: 3000 })
      return
    }

    const total = totalMembersCount.value ?? members.value.length
    if (total === 0) return

    const confirmed = confirm(`Вы уверены, что хотите удалить всех участников (${total}) из группы?`)
    if (!confirmed) return

    const myMxid = getCurrentMatrixUserId()

    try {
      const allMembers = await fetchAllChatMembers()

      await Promise.all(
        allMembers
          .filter(member => (member.user_uuid || String(member.user.id)) !== myMxid)
          .map(member =>
            coreChatStore.removeMemberFromChat(chatId, String(member.user.id)).catch(() => null),
          ),
      )

      members.value = myMxid
        ? allMembers.filter(member => (member.user_uuid || String(member.user.id)) === myMxid)
        : []
      totalMembersCount.value = members.value.length

      feedbackStore.showToast({ type: 'success', message: 'Все участники удалены из группы', time: 3000 })
      await loadChatMembers(true)
      onUpdated()
    } catch {
      feedbackStore.showToast({ type: 'error', message: 'Не удалось удалить всех участников', time: 3000 })
    }
  }

  function memberToEmployee(member: ChatMemberResponse): IEmployee {
    return {
      id: member.user.id,
      first_name: member.user.first_name || '',
      last_name: member.user.last_name || '',
      middle_name: member.user.middle_name || '',
      email: null,
      phone_number: '',
      avatar: member.avatar || member.user.avatar || '',
      position: member.user.position_name || null,
      department: member.user.department_name || null,
    } as unknown as IEmployee
  }

  function resetRemoving() {
    removingMemberIds.value.clear()
  }

  return {
    removingMemberIds,
    roleChangingIds,
    isRemovingMember,
    isCreator,
    isRoleChanging,
    setMemberAdmin,
    removeMember,
    removeAll,
    memberToEmployee,
    resetRemoving,
  }
}
